import { isValidElement, useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactElement } from 'react'

import { Hoverable } from './Hoverable'
import type { HoverPlacement } from './hoverTypes'

interface OverflowTipProps {
  /** Full text that appears in the tooltip when the child clips it. */
  label: string
  placement?: HoverPlacement
  group?: string
  offset?: number
  /** The truncated element. Must be a single React element whose ref can be
   * captured (a native span/div/button/etc.). Its CSS should already do the
   * ellipsis / clipping — this primitive only observes, it doesn't style. */
  children: ReactElement<Record<string, unknown>>
}

/**
 * Progressive-disclosure tooltip for truncated text.
 *
 * Wrap any element whose visible text is clipped by CSS (`overflow: hidden`,
 * `text-overflow: ellipsis`, line-clamp, etc.). This measures the child on
 * mount and on resize; if its content is actually overflowing, a **delayed**
 * hover reveals the full `label`. When the child fits, no hover is mounted —
 * so a row whose name happens to be short never gets a redundant bubble.
 *
 * The delay is deliberate: the text already tells the user what the row is,
 * and hover here is only for the times it's cut off. A slow reveal keeps the
 * UI quiet during casual mouse movement.
 */
export function OverflowTip({ label, placement = 'top', group, offset, children }: OverflowTipProps) {
  const anchorRef = useRef<HTMLElement | null>(null)
  const handleAnchorRef = useCallback((node: HTMLElement | null) => {
    anchorRef.current = node
  }, [])
  const overflowingRef = useRef(false)
  const [overflowing, setOverflowing] = useState(false)

  const measure = () => {
    const el = anchorRef.current
    if (!el) return
    // A 2px tolerance dodges subpixel rounding noise that would otherwise
    // fire the tooltip on rows whose label technically fits by half a pixel.
    const isOverflow = el.scrollWidth - el.clientWidth > 2 || el.scrollHeight - el.clientHeight > 2
    if (overflowingRef.current !== isOverflow) {
      overflowingRef.current = isOverflow
      setOverflowing(isOverflow)
    }
  }

  useLayoutEffect(() => {
    measure()
  }, [label])

  useEffect(() => {
    const el = anchorRef.current
    if (!el) return
    // Re-measure after fonts have loaded — first paint uses the fallback
    // metric and can undershoot / overshoot the final width.
    const raf = window.requestAnimationFrame(() => measure())
    let cleanupFonts: (() => void) | null = null
    const fonts = (document as unknown as { fonts?: { ready: Promise<unknown> } }).fonts
    if (fonts?.ready) {
      let cancelled = false
      void fonts.ready.then(() => { if (!cancelled) measure() })
      cleanupFonts = () => { cancelled = true }
    }
    let ro: ResizeObserver | null = null
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(() => measure())
      ro.observe(el)
    }
    return () => {
      window.cancelAnimationFrame(raf)
      cleanupFonts?.()
      ro?.disconnect()
    }
  }, [])

  if (!isValidElement(children)) return children

  return (
    <Hoverable
      variant="label"
      placement={placement}
      group={group}
      offset={offset}
      speed="delayed"
      disabled={!overflowing}
      bubbleClassName="hoverBubble--overflow"
      content={{ title: label }}
      onAnchorRef={handleAnchorRef}
    >
      {children}
    </Hoverable>
  )
}
